// Settings.js
import React, { useEffect, useState } from 'react';
import {
  FaCog,
  FaBell,
  FaPalette,
  FaGlobe,
  FaMoon,
  FaSun,
  FaSave,
  FaCheck,
  FaStore,
  FaInfoCircle
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './Settings.css';

const Settings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    orderNotifications: true,
    paymentNotifications: true,
    lowStockNotifications: true,
    theme: 'light',
    language: 'English',
    timezone: 'Asia/Karachi'
  });

  const [selectedShop, setSelectedShop] = useState(null);
  const [shops, setShops] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load saved settings
  useEffect(() => {
    try {
      const storedSettings =
        localStorage.getItem(
          'appSettings'
        );

      if (storedSettings) {
        const parsedSettings =
          JSON.parse(storedSettings);

        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }));
      }
    } catch (error) {
      console.error(
        'Error loading settings:',
        error
      );
    }
  }, []);

  // Fetch shops and selected shop
  useEffect(() => {
    const loadShopData = async () => {
      try {
        const response =
          await fetch(
            'https://super-backend.hira-tech.co.uk/user/shops/'
          );

        if (!response.ok) {
          return;
        }

        const data =
          await response.json();

        const shopsData =
          Array.isArray(data)
            ? data
            : data?.results ||
              data?.shops ||
              [];

        setShops(shopsData);

        const storedShopId =
          localStorage.getItem(
            'selectedShopId'
          );

        if (storedShopId) {
          const currentShop =
            shopsData.find(
              shop =>
                String(
                  shop.shop_id
                ) ===
                String(
                  storedShopId
                )
            );

          if (currentShop) {
            setSelectedShop(
              currentShop
            );
          }
        }
      } catch (error) {
        console.error(
          'Error loading shops:',
          error
        );
      }
    };

    loadShopData();
  }, []);

  // Listen for shop changes
  useEffect(() => {
    const handleShopChanged =
      event => {
        const newShopId =
          event?.detail?.shopId;

        if (!newShopId) {
          return;
        }

        const currentShop =
          shops.find(
            shop =>
              String(
                shop.shop_id
              ) ===
              String(
                newShopId
              )
          );

        if (currentShop) {
          setSelectedShop(
            currentShop
          );
        }
      };

    window.addEventListener(
      'shopChanged',
      handleShopChanged
    );

    return () => {
      window.removeEventListener(
        'shopChanged',
        handleShopChanged
      );
    };
  }, [shops]);

  const handleToggle =
    field => {
      setSettings(prev => ({
        ...prev,
        [field]:
          !prev[field]
      }));

      setSaved(false);
    };

  const handleChange =
    (field, value) => {
      setSettings(prev => ({
        ...prev,
        [field]: value
      }));

      setSaved(false);
    };

  const handleSave =
    async () => {
      try {
        setIsSaving(true);

        localStorage.setItem(
          'appSettings',
          JSON.stringify(
            settings
          )
        );

        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              500
            )
        );

        setSaved(true);

        await Swal.fire({
          icon: 'success',
          title: 'Settings Saved!',
          text:
            'Your settings have been saved successfully.',
          confirmButtonColor:
            '#004aad',
          timer: 1800,
          showConfirmButton:
            false
        });
      } catch (error) {
        console.error(
          'Error saving settings:',
          error
        );

        await Swal.fire({
          icon: 'error',
          title: 'Error!',
          text:
            'Failed to save settings. Please try again.',
          confirmButtonColor:
            '#004aad'
        });
      } finally {
        setIsSaving(false);
      }
    };

  const handleReset =
    async () => {
      const result =
        await Swal.fire({
          icon: 'warning',
          title: 'Reset Settings?',
          text:
            'All application settings will be restored to their defaults.',
          showCancelButton:
            true,
          confirmButtonColor:
            '#004aad',
          cancelButtonColor:
            '#d33',
          confirmButtonText:
            'Yes, reset',
          cancelButtonText:
            'Cancel'
        });

      if (!result.isConfirmed) {
        return;
      }

      const defaultSettings = {
        emailNotifications: true,
        orderNotifications: true,
        paymentNotifications: true,
        lowStockNotifications: true,
        theme: 'light',
        language: 'English',
        timezone: 'Asia/Karachi'
      };

      setSettings(
        defaultSettings
      );

      localStorage.setItem(
        'appSettings',
        JSON.stringify(
          defaultSettings
        )
      );

      setSaved(true);

      await Swal.fire({
        icon: 'success',
        title: 'Reset!',
        text:
          'Settings have been restored to default.',
        confirmButtonColor:
          '#004aad',
        timer: 1800,
        showConfirmButton:
          false
      });
    };

  return (
    <div className="settings-container">

      {/* Header */}
      <div className="settings-header">

        <div className="settings-header-left">

          <div className="settings-header-icon">
            <FaCog />
          </div>

          <div>
            <h2>
              Settings
            </h2>

            <p>
              Manage your application preferences and account settings
            </p>
          </div>

        </div>

        {saved && (
          <div className="settings-saved-badge">
            <FaCheck />
            Saved
          </div>
        )}

      </div>

      {/* Selected Shop */}
      <div className="settings-shop-card">

        <div className="settings-shop-icon">
          <FaStore />
        </div>

        <div className="settings-shop-info">

          <span className="settings-label">
            Current Shop
          </span>

          <strong>
            {selectedShop
              ? selectedShop.shop_name
              : 'No shop selected'}
          </strong>

        </div>

        <div className="settings-shop-id">

          Shop ID:{' '}

          {selectedShop
            ? selectedShop.shop_id
            : 'N/A'}

        </div>

      </div>

      {/* Main Content */}
      <div className="settings-content">

        {/* Notifications */}
        <div className="settings-section">

          <div className="settings-section-header">

            <div className="section-icon notification-icon">
              <FaBell />
            </div>

            <div>
              <h3>
                Notifications
              </h3>

              <p>
                Choose which notifications you want to receive
              </p>
            </div>

          </div>

          <div className="settings-options">

            <div className="settings-row">

              <div className="settings-row-text">

                <h4>
                  Email Notifications
                </h4>

                <p>
                  Receive important updates and account notifications by email.
                </p>

              </div>

              <button
                type="button"
                className={`settings-switch ${
                  settings.emailNotifications
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  handleToggle(
                    'emailNotifications'
                  )
                }
              >

                <span />

              </button>

            </div>

            <div className="settings-divider" />

            <div className="settings-row">

              <div className="settings-row-text">

                <h4>
                  New Order Notifications
                </h4>

                <p>
                  Get notified when a new order is received.
                </p>

              </div>

              <button
                type="button"
                className={`settings-switch ${
                  settings.orderNotifications
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  handleToggle(
                    'orderNotifications'
                  )
                }
              >

                <span />

              </button>

            </div>

            <div className="settings-divider" />

            <div className="settings-row">

              <div className="settings-row-text">

                <h4>
                  Payment Notifications
                </h4>

                <p>
                  Receive notifications when a payment is received.
                </p>

              </div>

              <button
                type="button"
                className={`settings-switch ${
                  settings.paymentNotifications
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  handleToggle(
                    'paymentNotifications'
                  )
                }
              >

                <span />

              </button>

            </div>

            <div className="settings-divider" />

            <div className="settings-row">

              <div className="settings-row-text">

                <h4>
                  Low Stock Notifications
                </h4>

                <p>
                  Get notified when products are running low on stock.
                </p>

              </div>

              <button
                type="button"
                className={`settings-switch ${
                  settings.lowStockNotifications
                    ? 'active'
                    : ''
                }`}
                onClick={() =>
                  handleToggle(
                    'lowStockNotifications'
                  )
                }
              >

                <span />

              </button>

            </div>

          </div>

        </div>

        {/* Appearance */}
        <div className="settings-section">

          <div className="settings-section-header">

            <div className="section-icon appearance-icon">
              <FaPalette />
            </div>

            <div>
              <h3>
                Appearance
              </h3>

              <p>
                Customize how the admin panel looks
              </p>
            </div>

          </div>

          <div className="settings-options">

            <div className="settings-row">

              <div className="settings-row-text">

                <h4>
                  Theme
                </h4>

                <p>
                  Choose your preferred application theme.
                </p>

              </div>

              <div className="theme-selector">

                <button
                  type="button"
                  className={`theme-option ${
                    settings.theme ===
                    'light'
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() =>
                    handleChange(
                      'theme',
                      'light'
                    )
                  }
                >
                  <FaSun />
                  Light
                </button>

                <button
                  type="button"
                  className={`theme-option ${
                    settings.theme ===
                    'dark'
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() =>
                    handleChange(
                      'theme',
                      'dark'
                    )
                  }
                >
                  <FaMoon />
                  Dark
                </button>

              </div>

            </div>

          </div>

        </div>

        {/* Regional Settings */}
        <div className="settings-section">

          <div className="settings-section-header">

            <div className="section-icon regional-icon">
              <FaGlobe />
            </div>

            <div>
              <h3>
                Regional Settings
              </h3>

              <p>
                Manage language and timezone preferences
              </p>
            </div>

          </div>

          <div className="settings-form-grid">

            <div className="settings-field">

              <label>
                Language
              </label>

              <select
                value={
                  settings.language
                }
                onChange={e =>
                  handleChange(
                    'language',
                    e.target.value
                  )
                }
              >

                <option value="English">
                  English
                </option>

              </select>

            </div>

            <div className="settings-field">

              <label>
                Timezone
              </label>

              <select
                value={
                  settings.timezone
                }
                onChange={e =>
                  handleChange(
                    'timezone',
                    e.target.value
                  )
                }
              >

                <option value="Asia/Karachi">
                  Pakistan Standard Time
                </option>

                <option value="Europe/London">
                  United Kingdom
                </option>

                <option value="Europe/Istanbul">
                  Turkey
                </option>

                <option value="Europe/Rome">
                  Italy
                </option>

                <option value="UTC">
                  UTC
                </option>

              </select>

            </div>

          </div>

        </div>

        {/* Info */}
        <div className="settings-info-box">

          <FaInfoCircle />

          <div>

            <strong>
              Settings
            </strong>

            <p>
              These application preferences are saved in this browser. Shop-specific currency settings are managed separately from the Currency Settings page.
            </p>

          </div>

        </div>

      </div>

      {/* Footer */}
      <div className="settings-footer">

        <button
          type="button"
          className="settings-reset-btn"
          onClick={
            handleReset
          }
        >
          Reset to Default
        </button>

        <button
          type="button"
          className="settings-save-btn"
          onClick={
            handleSave
          }
          disabled={isSaving}
        >

          {isSaving ? (
            <>
              Saving...
            </>
          ) : (
            <>
              <FaSave />
              Save Settings
            </>
          )}

        </button>

      </div>

    </div>
  );
};

export default Settings;